import * as React from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

import CarouselModal from "../components/CarouselModal";
import { Helmet } from "react-helmet-async";
import snigdhaMendocino from "../img/snigdha-mendocino.webp";
import snigdhaSonoma from "../img/snigdha-sonoma.webp";
import snigdhaSunflowers from "../img/snigdha-sunflowers.webp";
import snigdhaSushainLift from "../img/snigdha-sushain-lift.webp";
import snigdhaSushainMendocino from "../img/snigdha-sushain-mendocino.webp";
import snigdhaSushainNapa from "../img/snigdha-sushain-napa.webp";
import snigdhaSushainProposal from "../img/snigdha-sushain-proposal.webp";
import snigdhaSushainRing from "../img/snigdha-sushain-ring.webp";
import sushainMendocino from "../img/sushain-mendocino.webp";
import sushainNapa from "../img/sushain-napa.webp";
import sushainSlate from "../img/sushain-slate.webp";

import "../styles/about-us.css";

const MutedLink = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    className="link-secondary text-decoration-none"
    href={href}
    rel="noreferrer"
    target="_blank"
  >
    {children}
  </a>
);

const AboutUs = () => (
  <Container>
    <Helmet>
      <title>About Us</title>
    </Helmet>
    <Row className="about-us-row g-5" md={3} xs={1}>
      <Col>
        <CarouselModal
          images={[snigdhaSunflowers, snigdhaMendocino, snigdhaSonoma]}
          longText={
            <>
              <p>
                Snigdha is a software engineer at{" "}
                <MutedLink href="https://sentry.io/welcome">Sentry</MutedLink>.
                She loves bullet journals, hosting themed parties, and DIYs.
              </p>
              <p>
                She was on a Bollywood dance team through college and always
                planned to dance her way back to California soon. Convincing
                Sushain was tougher, but a few cats seem to have done the trick
                😸.
              </p>
              <p>
                Today, her dance journey centers around her YouTube channel,{" "}
                <MutedLink href="https://www.youtube.com/c/PhirSeNachle">
                  Phir Se Nachle
                </MutedLink>
                , where one of her videos has ~
                <MutedLink href="https://youtu.be/Ujav5kmp_-0">
                  2.5 million views
                </MutedLink>
                ! {"You'll"} find her designing costumes, making mixes, or
                editing, all while coming up with new routines.
              </p>{" "}
              <p>
                Whether {"it's "}
                dancing, knitting, painting, or embroidery, she always finds a
                way to let her creativity shine through!
              </p>
            </>
          }
          shortText={
            <>
              <p>
                Snigdha is a software engineer at{" "}
                <MutedLink href="https://sentry.io/welcome">Sentry</MutedLink>.
                She loves bullet journals, hosting themed parties, and DIYs.
              </p>
              <p>
                She was on {"CMU's"} Bollywood dance team and always planned to
                dance her way back to California. Convincing Sushain was
                tougher, but a few cats seem to have done the trick. 😸
              </p>
            </>
          }
          title={"Meet Snigdha Sharma"}
        />
      </Col>
      <Col>
        <CarouselModal
          images={[
            snigdhaSushainRing,
            snigdhaSushainLift,
            snigdhaSushainProposal,
            snigdhaSushainMendocino,
            snigdhaSushainNapa,
          ]}
          longText={
            <>
              <p>
                Our story begins with an awkward introduction during freshman
                orientation at Carnegie Mellon. Ask us about magicians and
                chocolate. 🙈
              </p>
              <p>
                Over the first few weeks, we ran into each other everywhere. We
                had different hobbies, majors, courses, and friends, and yet we
                never ran out of things to talk about. After two semesters of
                dining hall lunches and late-night study sessions, it was clear
                we were destined to be together.
              </p>
              <p>
                In May 2022, we took a trip to the British Isles. Somehow,
                amidst all the touristing, Sushain coordinated a private dance
                lesson with a renowned Bollywood choreographer! Those who know
                Sushain know he does not dance. Snigdha had been trying to get
                him to dance with her for years.
              </p>
              <p>
                After an hour and a half of laughter-filled dancing, they
                performed a ballroom/salsa/bollywood fusion piece set to Dekho
                Na. At the end, Sushain got down on one knee and popped the
                question! 💍
              </p>
            </>
          }
          shortText={
            <>
              <p>
                Our story begins with an awkward introduction during freshman
                orientation at Carnegie Mellon. Ask us about magicians and
                chocolate. 🙈
              </p>
              <p>
                Over the first few weeks, we ran into each other everywhere. We
                had different hobbies, majors, courses, and friends, and yet…
              </p>
            </>
          }
          title="How we met"
        />
      </Col>
      <Col>
        <CarouselModal
          images={[sushainMendocino, sushainSlate, sushainNapa]}
          longText={
            <>
              <p>
                Sushain is the tech lead for{" "}
                <MutedLink href="https://stripe.com">{`Stripe's`}</MutedLink>{" "}
                Build Infra team. A Texan at heart, he unwinds with country
                music, iced tea, and an epic sci-fi/fantasy novel.
              </p>
              <p>
                His greatest achievement is cajoling Snigdha into bringing{" "}
                <MutedLink href="https://instagram.com/loon.and.coon">
                  two charming cats
                </MutedLink>{" "}
                into their lives and fostering many more. 😼
              </p>
              <p>
                Sushain loves playing board games, especially euro/strategy
                games, so much so that he recently had to get a second bookshelf
                to house his collection!
              </p>
              <p>
                He is an avid numismatist. He has been collecting coins and
                bills since elementary school! Whenever he gets a chance, he
                shows off his everything from his $20 double-eagle from 1926 to
                a half-penny from 1854.
              </p>
            </>
          }
          shortText={
            <>
              <p>
                Sushain is the tech lead for{" "}
                <MutedLink href="https://stripe.com">{`Stripe's`}</MutedLink>{" "}
                Build Infra team. A Texan at heart, he unwinds with country
                music, iced tea, and an epic sci-fi/fantasy novel.
              </p>
              <p>
                His greatest achievement is cajoling Snigdha into bringing{" "}
                <MutedLink href="https://instagram.com/loon.and.coon">
                  two charming cats
                </MutedLink>{" "}
                into their lives and fostering many more. 😼
              </p>
            </>
          }
          title={"Meet Sushain Cherivirala"}
        />
      </Col>
    </Row>
  </Container>
);

export default AboutUs;
